import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import { msalConfig } from "@/config/authConfig";

// Pages
import LoginPage from "@/pages/LoginPage";
import TherapistDashboard from "@/pages/TherapistDashboard";
import FacilitatorDashboard from "@/pages/FacilitatorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import BookAppointment from "@/pages/BookAppointment";
import SessionDetails from "@/pages/SessionDetails";
import AdhocMeeting from "@/pages/AdhocMeeting";
import ErrorBoundary from "@/components/ErrorBoundary";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promise on load
msalInstance.initialize().then(() => {
  msalInstance.handleRedirectPromise().then((response) => {
    if (response) {
      msalInstance.setActiveAccount(response.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  }).catch((error) => {
    console.error("Redirect error:", error);
  });

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
});

// Helper function to check if user has any of the required roles
const userHasRole = (user, roles) => {
  if (!user || !user.roles) return false;
  return roles.some(role => user.roles.includes(role));
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !userHasRole(user, allowedRoles)) {
    // Redirect based on first available role
    if (user.roles?.includes("therapist")) {
      return <Navigate to="/therapist" replace />;
    }
    if (user.roles?.includes("facilitator")) {
      return <Navigate to="/facilitator" replace />;
    }
    if (user.roles?.includes("admin")) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Role-based redirect from home
const HomeRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on roles - facilitator takes priority over therapist for dual-role users
  if (user.roles?.includes("facilitator")) {
    return <Navigate to="/facilitator" replace />;
  }
  if (user.roles?.includes("therapist")) {
    return <Navigate to="/therapist" replace />;
  }
  if (user.roles?.includes("admin")) {
    return <Navigate to="/admin" replace />;
  }
  
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <AuthProvider>
          <WebSocketProvider>
            <NotificationProvider>
              <ImpersonationBanner />
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/callback" element={<HomeRedirect />} />

              {/* Home redirect */}
              <Route path="/" element={<HomeRedirect />} />

              {/* Therapist Routes - Therapists and Admin+Therapist can access */}
              <Route
                path="/therapist"
                element={
                  <ProtectedRoute allowedRoles={["therapist", "admin", "admin_therapist"]}>
                    <ErrorBoundary label="the therapist dashboard">
                      <TherapistDashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/session/:sessionId"
                element={
                  <ProtectedRoute allowedRoles={["therapist", "facilitator", "admin", "admin_therapist"]}>
                    <ErrorBoundary label="the session">
                      <SessionDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Facilitator Routes */}
              <Route
                path="/facilitator"
                element={
                  <ProtectedRoute allowedRoles={["facilitator", "admin"]}>
                    <ErrorBoundary label="the facilitator dashboard">
                      <FacilitatorDashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/facilitator/book"
                element={
                  <ProtectedRoute allowedRoles={["facilitator", "admin"]}>
                    <ErrorBoundary label="the booking page">
                      <BookAppointment />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes - Admin and Admin+Therapist can access */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin", "admin_therapist"]}>
                    <ErrorBoundary label="the admin dashboard">
                      <AdminDashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Ad-hoc meeting room (Req #8) — hosts Daily.co in-app
                  so 'leave' returns the user to their dashboard. */}
              <Route
                path="/adhoc-meeting/:meetingId"
                element={
                  <ProtectedRoute allowedRoles={["therapist", "facilitator", "admin", "admin_therapist"]}>
                    <ErrorBoundary label="the meeting room">
                      <AdhocMeeting />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
            </NotificationProvider>
          </WebSocketProvider>
        </AuthProvider>
      </BrowserRouter>
    </MsalProvider>
  );
}

export default App;
